import { de, enUS, es, fr, Locale } from 'date-fns/locale';

export enum AddressType {
  Property = 1,
  Mailing = 2,
  Residential = 3,
  Other = 4,
}

export const localeMappings: { [key: string]: Locale } = {
  enUS: enUS,
  fr: fr,
  de: de,
  es: es,
};

// object.keys does not narrow the type of the keys to the actual
// keys of the object. This function does.
export function objectKeys<T extends Object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string) => emailRegex.test(email);

export interface Address {
  type?: AddressType;
  unitNumber?: string;
  addressLine: string;
  city: string;
  state: string;
  country: string;
  postCode: string;
}

export interface AddressCoords {
  lat: number;
  long: number;
}
export enum DocumentImageSize {
  'thumb' = 'thumb',
  'xs' = 'xs',
  's' = 's',
  'm' = 'm',
  'l' = 'l',
  'xl' = 'xl',
}

export enum EditableFormType {
  AdditionalInformation = 1,
}

export enum WorkOrderStatus {
  Draft = 1,
  Submitted = 2,
  Paid = 3,
  Expired = 4,
  Cancelled = 5,
  Failed = 6,
}

export enum WorkOrderItemType {
  Membership = 1,
  Animal = 2,
  FeeOverride = 3,
}

export type HasRepresentative = 'yes' | 'no';

export type NewOrExistingMembership = 'new' | 'existing';

export enum EnterpriseActionTypeCode {
  Initial = 1,
  AuthorisedRepresentative = 2,
  Prefixes = 3,
  AdditionalInformation = 4,
  MemberAcknowledgement = 5,
  MembershipType = 6,
}

export interface ResponseValidationErrors {
  [fieldName: string]: string[];
}

export enum SortOrderEnum {
  ASC = 'asc',
  DESC = 'desc',
}

export enum TabOrientation {
  Horizontal = 'row',
  Vertical = 'column',
}

export interface FacebookPost {
  id: string;
  title: string;
  message: string;
  createdOn: string;
  imageLink: string;
}

export enum SexType {
  Male = 1,
  Female = 2,
}

export enum AnimalAltIdType {
  NLIS = 1,
}

export enum AnimalTraitType {
  Text = 1,
  Date = 2,
  Dropdown = 3,
  Radio = 4,
  Checkbox = 5,
  Textarea = 6,
  Number = 7,
  Range = 8,
  Select = 9,
  File = 10,
  Image = 11,
}

export interface BillingAddOnItem {
  name: string;
  cost: number;
  detail: string | null;
}

export interface BillingItem {
  name: string;
  description: string;
  cost: number;
}

export type RowAction = {
  label: string;
  isDisabled?: boolean;
  action: (animalEntityId: string) => void;
};

export enum AnimalActionType {
  Share = 1,
  Register = 2,
  Transfer = 3,
  ManageGroups = 4,
  AddToInventory = 5,
  Dispose = 6,
}

export enum AnimalTraitUnitOfMeasurement {
  None = 0,
  Kilograms = 1,
  Grams = 2,
  Liters = 3,
  Milliliters = 4,
  Meters = 5,
  Centimeters = 6,
  Millimeters = 7,
  Inches = 8,
  Feet = 9,
  Yards = 10,
  Miles = 11,
  Kilometers = 12,
  Pounds = 13,
  Ounces = 14,
  Gallons = 15,
  Quarts = 16,
  Pints = 17,
  Cups = 18,
  Tablespoons = 19,
  Teaspoons = 20,
  FluidOunces = 21,
  Milligrams = 22,
  Micrograms = 23,
  SquareMeters = 24,
  SquareCentimeters = 25,
  SquareMillimeters = 26,
  SquareInches = 27,
  SquareFeet = 28,
  SquareYards = 29,
  SquareMiles = 30,
  SquareKilometers = 31,
  CubicMeters = 32,
  CubicCentimeters = 33,
  CubicMillimeters = 34,
  CubicInches = 35,
  CubicFeet = 36,
  CubicYards = 37,
  CubicMiles = 38,
  CubicKilometers = 39,
  Degrees = 40,
  Radians = 41,
  Minutes = 42,
  Seconds = 43,
  Hours = 44,
  Days = 45,
  Weeks = 46,
  Months = 47,
  Years = 48,
  Decades = 49,
  Centuries = 50,
  Millenia = 51,
  Kelvin = 52,
  Celsius = 53,
  Fahrenheit = 54,
}

export enum MembershipTypeRecordStatus {
  Active = 1,
  Inactive = 2,
}

export enum UserRole {
  SocietyAdmin = 1,
  Breeder = 2,
  Admin = 18,
}

/**
 * Role IDs that grant per-organisation admin privileges within the registry
 * (e.g. access to `/config`).
 */
export const ADMIN_ROLES: ReadonlyArray<UserRole> = [
  UserRole.SocietyAdmin,
  UserRole.Admin,
];

export const isAdminRole = (roleId: number | null | undefined): boolean =>
  roleId != null && ADMIN_ROLES.includes(roleId as UserRole);

export enum UserRoleName {
  Admin = 'Breedlogic Admin',
  BaseUser = 'No-Role/Base User',
}

export enum NoteCategory {
  Dairy = 0,
  Beef = 1,
  Sheep = 2,
  Goat = 3,
  Pig = 4,
  Poultry = 5,
  Other = 6,
}

export enum ContactType {
  Email = 1,
  Phone = 2,
  Mobile = 3,
  Fax = 4,
}

export const phoneNumberTypes = [
  { id: ContactType.Phone, name: 'Home' },
  { id: ContactType.Mobile, name: 'Mobile' },
  { id: ContactType.Fax, name: 'Fax' },
];

export enum EventType {
  BUTTON_CLICK = 'ButtonClick',
  NAVIGATION_CLICK = 'NavigationClick',
  FORM_SUBMIT = 'FormSubmit',
  INPUT_CHANGE = 'InputChange',
  INPUT_FOCUS = 'InputFocus',
  DROPDOWN_SELECT = 'DropdownSelect',
  TAB_SWITCH = 'TabSwitch',
  DIALOG_INTERACTION = 'DialogInteraction',
  CHECKBOX_TOGGLE = 'CheckboxToggle',
  VIEW_CLICK = 'ViewClick',
  LOGIN = 'Login',
}

export const addressTypes = [
  { id: AddressType.Property, name: 'Property' },
  { id: AddressType.Mailing, name: 'Mailing' },
  { id: AddressType.Residential, name: 'Work' },
  { id: AddressType.Other, name: 'Other' },
];

export type EnterpriseDetailsTabValue =
  | 'profile'
  | 'paymentHistory'
  | 'settings'
  | 'notes';

export type EnterpriseProfileTabValue =
  | 'basicInfo'
  | 'contactInfo'
  | 'authReps'
  | 'prefixes'
  | 'memberInfo';
